// Where everything is served from.
//
// The route graph is the one that matters. Graphs are **regional** —
// there is no world graph — so the default is no graph at all, and the
// app has a designed state for that rather than an error. `?nomap=1`
// forces that state on a machine that does have one, because a state you
// cannot reach is a state nobody checks.

const params = new URLSearchParams(location.search);

/** The dev server's local graph, or "" in any production build. */
const devRouteGraph = typeof __DEV_ROUTE_GRAPH__ === "string" ? __DEV_ROUTE_GRAPH__ : "";

export const config = {
  engineBaseUrl: import.meta.env.VITE_ENGINE_BASE || "/rangefind/",
  /**
   * Empty means "this device holds no routing map for the area", which
   * is a legitimate and common answer, not a misconfiguration.
   */
  routeGraphUrl: params.has("nomap")
    ? ""
    : (import.meta.env.VITE_ROUTE_GRAPH_URL || devRouteGraph),
  /**
   * Which dataset the graph above is a build of — `quebec/car`.
   *
   * Empty means the run's epoch is the graph's content hash, which is
   * the old behaviour and correct for an operator serving a build they
   * pinned themselves. Setting it makes the epoch stable across rebuilds,
   * which is what a graph tracking a public index needs: the content hash
   * changes every time a road does, and the epoch is the topic namespace.
   *
   * **Every party on a run must set the same value or they will not meet.**
   * It travels with the URL in the catalogue for exactly that reason.
   */
  datasetId: import.meta.env.VITE_DATASET_ID || "",
  /** The static place index, read by byte range. Naming only, never load-bearing. */
  searchBaseUrl: import.meta.env.VITE_SEARCH_BASE ?? "https://osm.rangefind.dev",
  /**
   * The map style, served from **our own origin**.
   *
   * Empty by default, and that default is the honest one: with no style
   * published the map draws the roads this device already holds and says
   * that is what it is doing. Pointing this at a public tile CDN would
   * send every viewer's IP and every place they look at to a third party,
   * which is a change to the sentence at the bottom of this page rather
   * than a change of URL. Tiles are static files; they belong on the same
   * hosting as the rest of the static index (SERVICES.md).
   */
  mapStyleUrl: import.meta.env.VITE_MAP_STYLE ?? "",
  /**
   * §9.2 — what the graph is. Without it every estimate reports
   * `profileBasis: "unstated"` rather than quietly implying a match.
   */
  routeProfile: import.meta.env.VITE_ROUTE_PROFILE || "car",
  /**
   * A keeper to dial when the link carried no `?b=` hint.
   *
   * A capability is a key, not a location (§5.4), so a link says what a
   * run *is* and never where to find one. A cold tab holding a perfect
   * capability and no peer connects to nothing and waits forever, looking
   * exactly like a run that has not started.
   *
   * This is the opposite kind of object from everything else on this
   * page: an address is public, long-lived and grants nothing. A peer
   * answering on it still reads no thread it does not hold the key for,
   * which is why it is safe to compile in while the capability never is.
   *
   * The link's own hint wins where both exist — it names the operator's
   * seed for that job, which this default cannot know.
   */
  meshBootstrap: String(import.meta.env.VITE_MESH_BOOTSTRAP ?? "")
    .split(",")
    .map((address: string) => address.trim())
    .filter((address: string) => address.length > 0)
} as const;

/** Whether the page was opened with a capability at all. */
export const hasFragment = location.hash.replace(/^#/u, "").trim().length > 0;
